// Konstanta & helper Content Plan / Calendar Plan.
// SENGAJA tanpa dependensi server (tidak import `pg`) → aman di client & server.
import type { ContentStatus } from '@/lib/types'

// ── Status konten ───────────────────────────────────────────
export interface StatusStyle {
  label: string
  /** Kelas badge (bg + teks + border) — selaras tema Ide Board. */
  badge: string
  /** Kelas titik warna. */
  dot: string
  /** Warna aksen solid (untuk garis kartu / pill kalender). */
  accent: string
}

export const CONTENT_STATUS_CONFIG: Record<ContentStatus, StatusStyle> = {
  draft:         { label: 'Draft',    badge: 'bg-[#F4F3F0] text-[#6B6860] border border-[#E0DFD8]', dot: 'bg-[#C0BDB6]', accent: '#A8A79F' },
  planned:       { label: 'Rencana',  badge: 'bg-[#EFF6FF] text-[#2563EB] border border-[#BFCFFD]', dot: 'bg-[#2563EB]', accent: '#2563EB' },
  'in-progress': { label: 'Produksi', badge: 'bg-[#FEF3C7] text-[#B45309] border border-[#FDE68A]', dot: 'bg-[#F59E0B]', accent: '#F59E0B' },
  published:     { label: 'Tayang',   badge: 'bg-[#DCFCE7] text-[#16A34A] border border-[#BBF7D0]', dot: 'bg-[#16A34A]', accent: '#16A34A' },
}

// Urutan kolom kanban Content Plan (kiri → kanan = alur produksi).
export const CONTENT_STATUS_ORDER: ContentStatus[] = ['draft', 'planned', 'in-progress', 'published']

// ── Channel funnel ──────────────────────────────────────────
// Saran channel umum (dipakai sebagai quick-add di modal). Bebas tambah lain.
export const FUNNEL_CHANNELS = ['IG', 'WhatsApp', 'Reclub', 'TikTok', 'LinkedIn', 'Email', 'Ads'] as const

export interface ChannelTint { bg: string; border: string; text: string }
const CHANNEL_TINTS: ChannelTint[] = [
  { bg: '#EEF2FF', border: '#C7D2FE', text: '#4338CA' }, // indigo
  { bg: '#DCFCE7', border: '#BBF7D0', text: '#15803D' }, // green
  { bg: '#FEF3C7', border: '#FDE68A', text: '#B45309' }, // amber
  { bg: '#FFE4E6', border: '#FECDD3', text: '#BE123C' }, // rose
  { bg: '#E0F2FE', border: '#BAE6FD', text: '#0369A1' }, // sky
  { bg: '#F3E8FF', border: '#E9D5FF', text: '#7E22CE' }, // violet
  { bg: '#CCFBF1', border: '#99F6E4', text: '#0F766E' }, // teal
]

/** Warna konsisten per nama channel (channel sama → warna sama). */
export function channelTint(channel: string): ChannelTint {
  let h = 0
  for (let i = 0; i < channel.length; i++) h += channel.charCodeAt(i)
  return CHANNEL_TINTS[h % CHANNEL_TINTS.length]
}

// ── Tanggal (locale Indonesia, tanpa pergeseran timezone) ───
export const MONTHS_ID = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
]
export const MONTHS_ID_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']
// Senin dulu (mengikuti contoh kalender konten).
export const DAYS_ID_SHORT = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min']

/** Date → 'YYYY-MM-DD' berbasis waktu lokal (bukan UTC). */
export function toDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** 'YYYY-MM-DD' (atau ISO) → Date lokal pada tengah hari (hindari geser tanggal). */
export function parseDateKey(key: string): Date {
  const [y, m, d] = key.slice(0, 10).split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0)
}

/** Normalisasi nilai tanggal apa pun ke 'YYYY-MM-DD'. */
export function normalizeDateKey(value: string): string {
  return (value || '').slice(0, 10)
}

export interface MonthCell {
  date: Date
  key: string
  inMonth: boolean
  isToday: boolean
  /** 5 (Sabtu) / 6 (Minggu) = akhir pekan. */
  isWeekend: boolean
}

/**
 * Grid kalender bulanan: 6 baris × 7 kolom (Senin–Minggu).
 * `todayKey` opsional supaya pemanggil mengontrol "hari ini".
 */
export function buildMonthGrid(year: number, month: number, todayKey?: string): MonthCell[] {
  const first = new Date(year, month, 1)
  // getDay(): 0=Minggu..6=Sabtu → konversi ke indeks Senin-dulu (0=Senin..6=Minggu).
  const offset = (first.getDay() + 6) % 7
  const start = new Date(year, month, 1 - offset, 12, 0, 0)
  const tk = todayKey ?? toDateKey(new Date())
  const cells: MonthCell[] = []
  for (let i = 0; i < 42; i++) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i, 12, 0, 0)
    const dow = date.getDay()
    cells.push({
      date,
      key: toDateKey(date),
      inMonth: date.getMonth() === month,
      isToday: toDateKey(date) === tk,
      isWeekend: dow === 0 || dow === 6,
    })
  }
  return cells
}

/** 'YYYY-MM-DD' → '8 Jun 2026'. */
export function formatPlanDate(key: string): string {
  const d = parseDateKey(key)
  return `${d.getDate()} ${MONTHS_ID_SHORT[d.getMonth()]} ${d.getFullYear()}`
}

/** Label hari (Senin/…/Minggu) panjang dari sebuah key. */
const DAYS_ID_LONG = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu']
export function dayNameLong(key: string): string {
  return DAYS_ID_LONG[parseDateKey(key).getDay()]
}
